import * as pdfjsLib from 'pdfjs-dist/build/pdf.mjs';

import { escapeHTML } from './dom-utils.js';
import { pageJumpLabel, pageTypeBadgeHtml } from './page-type.js';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

let pdfDocumentPromise = null;
let pdfBytesKey = null;
let renderToken = 0;

function bytesKey(bytes) {
  if (!bytes) return '';
  return `${bytes.byteLength || bytes.length || 0}:${bytes[0] || 0}:${bytes[(bytes.length || bytes.byteLength || 1) - 1] || 0}`;
}

function asUint8Array(bytes) {
  if (!bytes) return null;
  if (bytes instanceof Uint8Array) return bytes;
  if (bytes instanceof ArrayBuffer) return new Uint8Array(bytes);
  return new Uint8Array(bytes);
}

async function getPdfDocument(bytes) {
  const data = asUint8Array(bytes);
  if (!data?.length) return null;

  const key = bytesKey(data);
  if (!pdfDocumentPromise || key !== pdfBytesKey) {
    pdfBytesKey = key;
    pdfDocumentPromise = pdfjsLib.getDocument({ data: new Uint8Array(data) }).promise;
  }
  return pdfDocumentPromise;
}

function normalizeForMatch(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .trim()
    .toLowerCase();
}

function buildTextIndex(textItems) {
  let text = '';
  const spans = [];

  for (const item of textItems) {
    const value = item.str || '';
    if (!value.trim()) continue;

    if (text && !/\s$/.test(text)) text += ' ';
    const start = text.length;
    text += value;
    spans.push({ item, start, end: text.length });
  }

  return { text, spans };
}

function findMatchingItems(textItems, queries) {
  const { text, spans } = buildTextIndex(textItems);
  const normalizedText = normalizeForMatch(text);
  const matched = new Set();

  for (const rawQuery of queries) {
    const query = normalizeForMatch(rawQuery).slice(0, 500);
    if (!query || query.length < 8) continue;

    let index = normalizedText.indexOf(query);
    let searchQuery = query;

    if (index < 0) {
      const tokens = query.split(/[^a-z0-9À-ÖØ-öø-ÿ]+/i).filter((token) => token.length >= 3);
      searchQuery = tokens.slice(0, 12).join(' ');
      index = searchQuery.length >= 8 ? normalizedText.indexOf(searchQuery) : -1;
    }

    if (index < 0) continue;

    const end = index + searchQuery.length;
    for (const [spanIndex, span] of spans.entries()) {
      if (span.end >= index && span.start <= end) matched.add(spanIndex);
    }

    if (matched.size) break;
  }

  return Array.from(matched).map((spanIndex) => spans[spanIndex].item);
}

function textItemRect(item, viewport) {
  const transform = pdfjsLib.Util.transform(viewport.transform, item.transform);
  const x = transform[4];
  const y = transform[5];
  const height = Math.hypot(transform[2], transform[3]) || item.height * viewport.scale || 10;
  const width = item.width * viewport.scale;
  return {
    left: x,
    top: y - height,
    width,
    height,
  };
}

function createRectHighlightLayer(viewport, rects) {
  const layer = document.createElement('div');
  layer.className = 'pdf-highlight-layer';

  for (const rect of rects || []) {
    const x0 = Number(rect.x0_ratio);
    const y0 = Number(rect.y0_ratio);
    const x1 = Number(rect.x1_ratio);
    const y1 = Number(rect.y1_ratio);
    if (![x0, y0, x1, y1].every(Number.isFinite)) continue;

    const left = Math.min(x0, x1) * viewport.width;
    const top = Math.min(y0, y1) * viewport.height;
    const width = Math.abs(x1 - x0) * viewport.width;
    const height = Math.abs(y1 - y0) * viewport.height;
    if (width <= 0 || height <= 0) continue;

    const mark = document.createElement('div');
    mark.className = 'pdf-text-highlight';
    mark.style.left = `${left}px`;
    mark.style.top = `${top}px`;
    mark.style.width = `${width}px`;
    mark.style.height = `${Math.max(height, 6)}px`;
    layer.appendChild(mark);
  }

  return layer;
}

async function createTextLayer(page, viewport) {
  const textContent = await page.getTextContent();
  const layer = document.createElement('div');
  layer.className = 'pdf-text-layer';

  const textLayer = new pdfjsLib.TextLayer({
    textContentSource: textContent,
    container: layer,
    viewport,
  });
  await textLayer.render();

  return { layer, textItems: textContent.items || [] };
}

function createTextHighlightLayer(page, viewport, textItems, focus) {
  const layer = document.createElement('div');
  layer.className = 'pdf-highlight-layer';

  const queries = [
    focus?.context,
    focus?.text,
    focus?.fullContext,
  ].filter(Boolean);
  const matchedItems = findMatchingItems(textItems, queries);

  for (const item of matchedItems) {
    const rect = textItemRect(item, viewport);
    if (!Number.isFinite(rect.left) || !Number.isFinite(rect.top) || rect.width <= 0 || rect.height <= 0) continue;

    const mark = document.createElement('div');
    mark.className = 'pdf-text-highlight';
    mark.style.left = `${rect.left}px`;
    mark.style.top = `${rect.top}px`;
    mark.style.width = `${rect.width}px`;
    mark.style.height = `${Math.max(rect.height, 6)}px`;
    layer.appendChild(mark);
  }

  return layer;
}

function focusRectsForPage(focus, pageNum) {
  const rects = focus?.highlightRects || focus?.rects || [];
  return (Array.isArray(rects) ? rects : []).filter((rect) => {
    if (!rect?.page) return true;
    return String(rect.page) === String(pageNum);
  });
}

function centerPdfPageHorizontally(article, target = null) {
  if (!article) return;
  const content = article.querySelector('.pdf-canvas-wrap');
  if (!content) return;

  const targetRect = target?.getBoundingClientRect?.();
  const contentRect = content.getBoundingClientRect();
  const targetCenter = targetRect
    ? (targetRect.left + targetRect.width / 2) - contentRect.left
    : content.scrollWidth / 2;

  article.scrollLeft = Math.max(0, targetCenter - article.clientWidth / 2);
}

function scrollFocusedHighlightIntoView(article, { behavior = 'auto' } = {}) {
  const target = article?.querySelector?.('.pdf-text-highlight') || article;
  if (!target) return;

  requestAnimationFrame(() => {
    centerPdfPageHorizontally(article, target);
    target.scrollIntoView({
      behavior,
      block: 'center',
      inline: 'center',
    });
    article.classList.add('focused');
    setTimeout(() => article.classList.remove('focused'), 1200);
  });
}

function clearPdfHighlights(wrap) {
  for (const layer of wrap?.querySelectorAll?.('.pdf-highlight-layer') || []) {
    layer.remove();
  }
}

function applyFocusToRenderedPdf(wrap, focus, { behavior = 'auto' } = {}) {
  if (!wrap || !focus?.page) return false;

  const page = Number(focus.page);
  if (!Number.isFinite(page)) return false;

  const article = wrap.querySelector(`#page-${page}.pdf-doc-page`);
  if (!article) return false;

  clearPdfHighlights(wrap);

  const canvasWrap = article.querySelector('.pdf-canvas-wrap');
  if (canvasWrap && article.__pdfViewport) {
    const rects = focusRectsForPage(focus, focus.page);
    if (rects.length) {
      canvasWrap.appendChild(createRectHighlightLayer(article.__pdfViewport, rects));
    } else if (article.__pdfTextItems) {
      canvasWrap.appendChild(createTextHighlightLayer(null, article.__pdfViewport, article.__pdfTextItems, focus));
    }
  }

  scrollFocusedHighlightIntoView(article, { behavior });
  return true;
}

export function focusRenderedPdfPage(focus, { behavior = 'auto', wrap = null } = {}) {
  const targetWrap = wrap || document.getElementById('doc-pages');
  return applyFocusToRenderedPdf(targetWrap, focus, { behavior });
}

export async function renderPdfPages({
  pages,
  pdfBytes,
  focus,
  anchorPage = null,
  onPageCentered,
  scale = 1.15,
  wrap,
  jump,
}) {
  const token = ++renderToken;
  if (!wrap.querySelector('.pdf-doc-page')) {
    wrap.innerHTML = '<div class="pdf-loading">Rendering PDF...</div>';
  }

  const pdf = await getPdfDocument(pdfBytes);
  if (token !== renderToken) return;

  if (!pdf) {
    wrap.innerHTML = '<div class="pdf-unavailable">PDF bytes are not available for this document.</div>';
    return;
  }

  const total = pdf.numPages;
  const pageNumbers = pages?.length
    ? pages.map((p, idx) => p.page_number ?? (idx + 1)).filter((pageNum) => pageNum >= 1 && pageNum <= total)
    : Array.from({ length: total }, (_, idx) => idx + 1);
  const pageInfoByNumber = new Map(
    (pages || []).map((page, idx) => [Number(page.page_number ?? (idx + 1)), page]),
  );
  const scaleKey = String(scale);

  if (
    wrap.dataset.pdfScale === scaleKey
    && wrap.querySelectorAll('.pdf-doc-page').length === pageNumbers.length
    && applyFocusToRenderedPdf(wrap, focus, { behavior: 'auto' })
  ) {
    if (focus?.page) onPageCentered?.(Number(focus.page));
    return;
  }

  let focusedArticleId = null;
  let focusedScrolled = false;
  let restoredAnchor = false;

  wrap.innerHTML = '';
  wrap.dataset.pdfScale = scaleKey;
  jump.innerHTML = '';

  for (const pageNum of pageNumbers) {
    if (token !== renderToken) return;

    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    const article = document.createElement('article');
    article.className = 'doc-page pdf-doc-page';
    article.id = `page-${pageNum}`;
    const pageInfo = pageInfoByNumber.get(Number(pageNum));

    const canvasWrap = document.createElement('div');
    canvasWrap.className = 'pdf-canvas-wrap';
    canvasWrap.style.width = `${viewport.width}px`;
    canvasWrap.style.height = `${viewport.height}px`;

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;

    canvasWrap.appendChild(canvas);
    article.innerHTML = `<div class="page-head">Page ${escapeHTML(String(pageNum))} / ${total}${pageTypeBadgeHtml(pageInfo)}</div>`;
    article.appendChild(canvasWrap);
    wrap.appendChild(article);

    const opt = document.createElement('option');
    opt.value = String(pageNum);
    opt.textContent = pageJumpLabel(pageNum, pageInfo);
    jump.appendChild(opt);

    if (!restoredAnchor && anchorPage && String(anchorPage) === String(pageNum)) {
      restoredAnchor = true;
      requestAnimationFrame(() => {
        article.scrollIntoView({ behavior: 'auto', block: 'start' });
        centerPdfPageHorizontally(article);
        onPageCentered?.(Number(anchorPage));
      });
    }

    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    if (token !== renderToken) return;

    const { layer: textLayer, textItems } = await createTextLayer(page, viewport);
    if (token !== renderToken) return;
    article.__pdfViewport = viewport;
    article.__pdfTextItems = textItems;
    canvasWrap.appendChild(textLayer);

    if (focus?.page && String(focus.page) === String(pageNum)) {
      const rects = focusRectsForPage(focus, pageNum);
      if (rects.length) {
        canvasWrap.appendChild(createRectHighlightLayer(viewport, rects));
      } else {
        canvasWrap.appendChild(createTextHighlightLayer(page, viewport, textItems, focus));
      }
      focusedArticleId = article.id;
      scrollFocusedHighlightIntoView(article, { behavior: 'auto' });
      focusedScrolled = true;
      onPageCentered?.(Number(focus.page));
    }
  }

  if (focusedArticleId && !focusedScrolled) {
    const focusedArticle = document.getElementById(focusedArticleId);
    scrollFocusedHighlightIntoView(focusedArticle, { behavior: 'auto' });
    if (focus?.page) onPageCentered?.(Number(focus.page));
  } else if (anchorPage) {
    onPageCentered?.(Number(anchorPage));
  }
}
